import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, ShieldCheck, Trash2, Users,
  UserCog,
} from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { usePlatformRole } from '@/store/auth';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Field, Input, Select } from '@/components/ui/Input';
import { ConfirmDialog, Modal } from '@/components/ui/Modal';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/ui/States';
import { Table, TableWrapper, Td, Th, Tr } from '@/components/ui/Table';
import { useToast } from '@/components/ui/Toast';
import { formatRelative } from '@/lib/format';

const PERAN = {
  user: { label: 'User', tone: 'neutral' },
  admin: { label: 'Admin', tone: 'accent' },
  dev: { label: 'Dev', tone: 'info' },
  root: { label: 'Root', tone: 'danger' },
};

const StatChip = ({ label, value }) => (
  <div className="rounded-xl border border-[var(--border-subtle)] bg-[var(--surface-raised)] px-4 py-3">
    <p className="text-xl font-bold text-[var(--text-primary)]">{value ?? 0}</p>
    <p className="text-xs text-[var(--text-muted)]">{label}</p>
  </div>
);

/**
 * Daftar seluruh pengguna platform.
 *
 * Admin hanya bisa MELIHAT: jumlah, peran, dan keadaan tiap akun. Tombol
 * tindakan - setel ulang sandi, ganti peran, hapus - hanya dirender untuk
 * root, dan servernya menolak peran lain sekalipun tombolnya dipaksa muncul.
 */
export const PlatformUsersPage = () => {
  const peranSaya = usePlatformRole();
  const bolehKelola = peranSaya === 'root';
  const toast = useToast();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [role, setRole] = useState('');
  const [kelolaTarget, setKelolaTarget] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [hapusTarget, setHapusTarget] = useState(null);
  const [sandiBaru, setSandiBaru] = useState('');

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['platform-users', { search, role }],
    queryFn: () => api.get('/platform/users', { params: { search, role, limit: 50 } }),
  });

  const pengguna = data?.data ?? [];
  const stats = data?.meta?.stats ?? {};

  const segarkan = () => queryClient.invalidateQueries({ queryKey: ['platform-users'] });

  const ubahPeran = useMutation({
    mutationFn: ({ id, platformRole }) => api.patch(`/platform/users/${id}/role`, { platformRole }),
    onSuccess: (hasil) => {
      toast.success('Peran diubah', { description: hasil?.data?.message });
      segarkan();
    },
    onError: (err) =>
      toast.error('Gagal mengubah peran', {
        description: err instanceof ApiError ? err.message : 'Coba lagi.',
      }),
  });

  const resetSandi = useMutation({
    mutationFn: () => api.post(`/platform/users/${resetTarget.id}/password`, { password: sandiBaru }),
    onSuccess: (hasil) => {
      toast.success('Sandi disetel ulang', { description: hasil?.data?.message });
      setResetTarget(null);
      setSandiBaru('');
    },
    onError: (err) =>
      toast.error('Gagal menyetel ulang', {
        description: err instanceof ApiError ? err.message : 'Coba lagi.',
      }),
  });

  const hapus = useMutation({
    mutationFn: () => api.delete(`/platform/users/${hapusTarget.id}`),
    onSuccess: (hasil) => {
      toast.success('Akun dihapus', { description: hasil?.data?.message });
      setHapusTarget(null);
      segarkan();
    },
    onError: (err) => {
      toast.error('Gagal menghapus', {
        description: err instanceof ApiError ? err.message : 'Coba lagi.',
      });
      setHapusTarget(null);
    },
  });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-[var(--text-primary)]">Pengguna Platform</h1>
        <p className="mt-1 text-sm text-[var(--text-secondary)]">
          {bolehKelola
            ? 'Seluruh akun terdaftar. Sebagai root, Anda bisa mengubah peran, menyetel ulang sandi, dan menghapus akun.'
            : 'Seluruh akun terdaftar. Peran admin hanya bisa melihat; pengelolaan akun dilakukan root.'}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <StatChip label="Total akun" value={stats.total} />
        <StatChip label="User" value={stats.perRole?.user} />
        <StatChip label="Admin" value={stats.perRole?.admin} />
        <StatChip label="Dev" value={stats.perRole?.dev} />
        <StatChip label="Root" value={stats.perRole?.root} />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cari nama atau email"
          aria-label="Cari pengguna"
          className="h-10 w-full px-3 text-sm sm:max-w-xs"
        />
        <div className="w-40">
          <Select value={role} onChange={(e) => setRole(e.target.value)} aria-label="Saring peran">
            <option value="">Semua peran</option>
            {Object.entries(PERAN).map(([nilai, meta]) => (
              <option key={nilai} value={nilai}>
                {meta.label}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <Card>
        {isLoading ? (
          <TableSkeleton rows={6} columns={bolehKelola ? 6 : 5} />
        ) : isError ? (
          <ErrorState message={error?.message} requestId={error?.requestId} onRetry={refetch} />
        ) : pengguna.length === 0 ? (
          <EmptyState icon={Users} title="Tidak ada yang cocok" description="Coba kata kunci lain." />
        ) : (
          <TableWrapper>
            <Table>
              <thead>
                <Tr>
                  <Th>Pengguna</Th>
                  <Th>Peran</Th>
                  <Th>Status</Th>
                  <Th>Organisasi</Th>
                  <Th>Terakhir Masuk</Th>
                  {bolehKelola && <Th>Tindakan</Th>}
                </Tr>
              </thead>
              <tbody>
                {pengguna.map((u) => {
                  const meta = PERAN[u.platformRole] ?? PERAN.user;
                  return (
                    <Tr key={u.id}>
                      <Td>
                        <p className="font-semibold text-[var(--text-primary)]">{u.name}</p>
                        <p className="text-xs text-[var(--text-muted)]">
                          {u.email}
                          {u.emailVerified && (
                            <ShieldCheck
                              className="ml-1 inline size-3.5 text-success-500"
                              aria-label="Email terverifikasi"
                            />
                          )}
                        </p>
                      </Td>
                      <Td>
                        {bolehKelola && u.platformRole !== 'root' ? (
                          <div className="w-28">
                            <Select
                              value={u.platformRole}
                              aria-label={`Peran ${u.email}`}
                              onChange={(e) =>
                                ubahPeran.mutate({ id: u.id, platformRole: e.target.value })
                              }
                            >
                              {['user', 'admin', 'dev', 'root'].map((r) => (
                                <option key={r} value={r}>
                                  {PERAN[r].label}
                                </option>
                              ))}
                            </Select>
                          </div>
                        ) : (
                          <Badge tone={meta.tone} size="sm">
                            {meta.label}
                          </Badge>
                        )}
                      </Td>
                      <Td className="text-[var(--text-secondary)]">{u.status}</Td>
                      <Td className="text-[var(--text-secondary)]">{u.organizations}</Td>
                      <Td className="text-[var(--text-muted)]">
                        {u.lastLoginAt ? formatRelative(u.lastLoginAt) : 'belum pernah'}
                      </Td>
                      {bolehKelola && (
                        <Td>
                          {u.platformRole !== 'root' && (
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                icon={UserCog}
                                iconOnly
                                aria-label={`Kelola ${u.email}`}
                                title="Kelola akun"
                                onClick={() => setKelolaTarget(u)}
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                icon={KeyRound}
                                iconOnly
                                aria-label={`Setel ulang sandi ${u.email}`}
                                title="Setel ulang sandi"
                                onClick={() => setResetTarget(u)}
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                icon={Trash2}
                                iconOnly
                                aria-label={`Hapus ${u.email}`}
                                title="Hapus akun"
                                className="text-danger-500 hover:text-danger-600"
                                onClick={() => setHapusTarget(u)}
                              />
                            </div>
                          )}
                        </Td>
                      )}
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
          </TableWrapper>
        )}
      </Card>

      <Modal
        open={Boolean(resetTarget)}
        onClose={() => setResetTarget(null)}
        title="Setel Ulang Kata Sandi"
        description={`Sandi baru untuk ${resetTarget?.email}. Semua sesinya akan dicabut.`}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setResetTarget(null)}>
              Batal
            </Button>
            <Button
              loading={resetSandi.isPending}
              disabled={sandiBaru.length < 8}
              onClick={() => resetSandi.mutate()}
            >
              Setel Ulang
            </Button>
          </div>
        }
      >
        <Field
          label="Kata Sandi Baru"
          required
          hint="Minimal 8 karakter. Disimpan sebagai hash Argon2id, tidak pernah sebagai teks biasa."
        >
          {(props) => (
            <Input
              {...props}
              type="password"
              value={sandiBaru}
              onChange={(e) => setSandiBaru(e.target.value)}
              autoComplete="new-password"
              placeholder="Minimal 8 karakter"
            />
          )}
        </Field>
      </Modal>

      <ConfirmDialog
        open={Boolean(hapusTarget)}
        onClose={() => setHapusTarget(null)}
        onConfirm={() => hapus.mutate()}
        loading={hapus.isPending}
        destructive
        title={`Hapus akun ${hapusTarget?.email}?`}
        description="Akun, keanggotaan, dan seluruh sesinya dihapus permanen. Data organisasi yang pernah ia buat tetap ada."
        confirmLabel="Ya, hapus"
      />
      {kelolaTarget && (
        <DialogKelolaAkun user={kelolaTarget} onClose={() => setKelolaTarget(null)} />
      )}

    </div>
  );
};

/**
 * Kelola satu akun: profil, kondisi, dan pemberian paket manual.
 *
 * Ketiganya dikumpulkan dalam satu dialog karena selalu dilakukan bersamaan
 * saat root menangani satu pelanggan. Yang tidak dikumpulkan di sini adalah
 * peran dan kata sandi: keduanya punya akibat yang jauh lebih besar dan
 * sudah punya jalurnya sendiri di tabel.
 */
const DialogKelolaAkun = ({ user, onClose }) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [nama, setNama] = useState(user.name ?? '');
  const [email, setEmail] = useState(user.email ?? '');
  const [status, setStatus] = useState(user.status ?? 'active');
  const [paket, setPaket] = useState('growth');
  const [hari, setHari] = useState('30');
  const [alasan, setAlasan] = useState('');

  const segarkan = () => queryClient.invalidateQueries({ queryKey: ['platform-users'] });

  const simpanProfil = useMutation({
    mutationFn: () => api.patch(`/platform/users/${user.id}/profile`, { name: nama, email }),
    onSuccess: () => {
      toast.success('Profil disimpan');
      segarkan();
    },
    onError: (err) =>
      toast.error('Gagal menyimpan profil', {
        description: err instanceof ApiError ? err.message : 'Coba lagi.',
      }),
  });

  const simpanStatus = useMutation({
    mutationFn: (nilai) => api.patch(`/platform/users/${user.id}/status`, { status: nilai }),
    onSuccess: (res) => {
      setStatus(res?.data?.status);
      toast.success('Kondisi akun diubah');
      segarkan();
    },
    onError: (err) =>
      toast.error('Gagal mengubah kondisi', {
        description: err instanceof ApiError ? err.message : 'Coba lagi.',
      }),
  });

  const beriPaket = useMutation({
    mutationFn: () =>
      api.post(`/platform/organizations/${user.organizationId}/grant-plan`, {
        planId: paket,
        hari: Number(hari),
        alasan,
      }),
    onSuccess: () => {
      toast.success('Paket diberikan', {
        description: 'Tercatat di audit log, dan tidak masuk laporan pemasukan.',
      });
      setAlasan('');
      segarkan();
    },
    onError: (err) =>
      toast.error('Gagal memberi paket', {
        description: err instanceof ApiError ? err.message : 'Coba lagi.',
      }),
  });

  return (
    <Modal open onClose={onClose} title={`Kelola ${user.name}`} description={user.email} size="lg">
      <div className="space-y-6">
        <section className="space-y-3">
          <h3 className="text-xs font-bold tracking-wide text-[var(--text-muted)] uppercase">
            Profil
          </h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Nama">
              {(props) => <Input {...props} value={nama} onChange={(e) => setNama(e.target.value)} />}
            </Field>
            <Field
              label="Email"
              hint="Mengganti email membuat statusnya kembali belum terverifikasi."
            >
              {(props) => (
                <Input {...props} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              )}
            </Field>
          </div>
          <div className="flex justify-end">
            <Button
              size="sm"
              loading={simpanProfil.isPending}
              disabled={nama === user.name && email === user.email}
              onClick={() => simpanProfil.mutate()}
            >
              Simpan profil
            </Button>
          </div>
        </section>

        <section className="space-y-3 border-t border-[var(--border-subtle)] pt-5">
          <h3 className="text-xs font-bold tracking-wide text-[var(--text-muted)] uppercase">
            Kondisi akun
          </h3>
          <p className="text-sm text-[var(--text-secondary)]">
            Menangguhkan bisa dibatalkan, menghapus tidak. Sebagian besar kasus yang terasa menuntut
            penghapusan sebenarnya cukup ditangguhkan.
          </p>
          <div className="flex flex-wrap gap-2">
            {[
              ['active', 'Aktif'],
              ['inactive', 'Nonaktif'],
              ['blocked', 'Diblokir'],
            ].map(([nilai, label]) => (
              <Button
                key={nilai}
                size="sm"
                variant={status === nilai ? 'primary' : 'secondary'}
                loading={simpanStatus.isPending && simpanStatus.variables === nilai}
                onClick={() => simpanStatus.mutate(nilai)}
              >
                {label}
              </Button>
            ))}
          </div>
        </section>

        {user.organizationId && (
          <section className="space-y-3 border-t border-[var(--border-subtle)] pt-5">
            <h3 className="text-xs font-bold tracking-wide text-[var(--text-muted)] uppercase">
              Beri paket manual
            </h3>
            <p className="text-sm text-[var(--text-secondary)]">
              Untuk akun internal, kompensasi, atau uji coba. Sengaja tidak melewati alur transaksi:
              uang yang tidak pernah masuk tidak boleh muncul di laporan pemasukan.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Paket">
                {(props) => (
                  <Select {...props} value={paket} onChange={(e) => setPaket(e.target.value)}>
                    {['free', 'starter', 'growth', 'pro', 'business'].map((p) => (
                      <option key={p} value={p} className="capitalize">
                        {p}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label="Lama (hari)">
                {(props) => (
                  <Input
                    {...props}
                    type="number"
                    min="1"
                    value={hari}
                    onChange={(e) => setHari(e.target.value)}
                  />
                )}
              </Field>
            </div>
            <Field label="Alasan" required hint="Tercatat di audit log supaya bisa ditelusuri nanti.">
              {(props) => (
                <Input
                  {...props}
                  value={alasan}
                  onChange={(e) => setAlasan(e.target.value)}
                  placeholder="Kompensasi gangguan 20 Agustus"
                />
              )}
            </Field>
            <div className="flex justify-end">
              <Button
                size="sm"
                loading={beriPaket.isPending}
                disabled={alasan.trim().length < 3}
                onClick={() => beriPaket.mutate()}
              >
                Beri paket
              </Button>
            </div>
          </section>
        )}
      </div>
    </Modal>
  );
};
